import { z } from 'zod';
import type { MessageDescriptor } from 'react-intl';
import config from '@packages/config/src';
import {
  fieldMessageGeneral,
  fieldMessagesInvalid,
  fieldMessagesLength,
  fieldMessagesRequired,
} from './AddressValidation.messages';

export const FieldErrorReason = {
  required: 'Required',
  invalid: 'Invalid',
  streetPP: 'StreetPackstationPostfiliale',
  length: 'Length',
};

/**
 * Length limits differ per field, and a plain `fieldMessagesLength[fieldName]` lookup would miss
 * the names: callers pass `firstName` / `lastName` while both share the `name` descriptor.
 */
const lengthMessageByField = {
  firstName: fieldMessagesLength.name,
  lastName: fieldMessagesLength.name,
  street: fieldMessagesLength.street,
  city: fieldMessagesLength.city,
} as const satisfies Record<string, MessageDescriptor>;

type LengthCheckedField = keyof typeof lengthMessageByField;

const hasLengthMessage = (field: string): field is LengthCheckedField =>
  field in lengthMessageByField;

export const getFieldError = (reason: string, fieldName: string): MessageDescriptor => {
  if (reason === FieldErrorReason.required) {
    return fieldMessagesRequired[fieldName as keyof typeof fieldMessagesRequired];
  }
  if (reason === FieldErrorReason.invalid) {
    return fieldMessagesInvalid[fieldName as keyof typeof fieldMessagesInvalid];
  }
  if (reason === FieldErrorReason.length) {
    return hasLengthMessage(fieldName)
      ? lengthMessageByField[fieldName]
      : fieldMessageGeneral.somethingWrong;
  }
  if (reason === FieldErrorReason.streetPP) {
    return fieldMessagesInvalid.streetPackOrPost;
  }
  return fieldMessageGeneral.somethingWrong;
};

const nameRegex = /^[A-Za-zÄÖÜäöü][A-Za-z'.\-\sßÄÖÜäöü]*$/;

export const phoneNumberCharacterPattern = /^[0-9()+\-/ ]*$/;

// First and last name are limited to 2-30 characters in the inline validation (IOGZ-423).
export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 30;

/**
 * Street and city share one rule, taken verbatim from the legacy checkout
 * (fragment-order-checkout/src/components/RegisterForm/registrationRules.tsx) so both checkouts
 * accept the same input: Latin-1 accents plus Ć-ĉ, up to 40 characters.
 *
 * Known limitation inherited from there: Latin letters outside Latin-1 are missing, so a Polish
 * "Świętokrzyska" fails. That only bites for an address in the tenant's own country now, because
 * addresses abroad skip these checks entirely (see below).
 */
const addressTextRegex = /^[A-Za-z0-9'´À-ÖØ-öø-ÿĆ-ĉ/., -]*$/u;
/** Kept out of {@link addressTextRegex} so a too-long value reports Length, not Invalid. */
const ADDRESS_TEXT_MAX_LENGTH = 40;
const parcelShopRegex = /^(?!.*(packstation|postfiliale)).*$/i;
/** Applied to the {@link normalizeHouseNumber} output, so it needs no case or whitespace slack. */
const houseNumberRegex = /^[1-9]([a-z0-9.,;/ +-]*[a-z0-9])?$/;
const zipCodeRegex = new RegExp(`^[0-9]{${config.order.addressValidation.zipCode.length}}$`);

/** Country the shop itself is operated in, derived from the tenant's default locale ('de-DE' -> 'DE'). */
export const tenantCountryCode = config.i18n.defaultLocale.split('-')[1] ?? '';

/**
 * An empty country counts as domestic: the country field has its own required check and
 * until it is answered the tenant's own country is the only sensible assumption.
 */
export const isTenantCountry = (countryCode?: string | null) =>
  !countryCode || countryCode.toUpperCase() === tenantCountryCode;

/**
 * Address field that is either mandatory and format checked, or – when the address is not
 * validated at all – a plain string that accepts anything, including an empty value.
 */
const addressField = (validate: boolean, formats: Array<[RegExp, string]>, maxLength?: number) => {
  if (!validate) {
    return z.string();
  }

  const field = formats.reduce(
    (checked, [format, message]) => checked.regex(format, { error: message }),
    z.string().min(1, { error: FieldErrorReason.required }),
  );

  return maxLength ? field.max(maxLength, { error: FieldErrorReason.length }) : field;
};

/**
 * House numbers are compared and stored lowercase, and a leading zero carries no meaning – "012"
 * is house 12 – so both are normalized away before the value is validated and before it goes to
 * `ValidateAddress` and `Register` (IOGZ-455). Uppercase input is explicitly allowed.
 *
 * Only padding is dropped, never the numeric part itself: "0" and "0a" keep their zero, so what
 * the customer typed survives normalization and acceptance stays a separate decision.
 */
export const normalizeHouseNumber = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    .replace(/^0+(?=\d)/, '');

/** House number field that validates and returns the normalized value, never the raw input. */
const houseNumberField = (validate: boolean) =>
  z.preprocess(
    (value: string | undefined) =>
      typeof value === 'string' ? normalizeHouseNumber(value) : value,
    addressField(validate, [[houseNumberRegex, FieldErrorReason.invalid]]),
  );

export type AddressValidationSchemaOptions = {
  /**
   * Validate street, house number, zip code and city at all (default).
   *
   * Turn it off for an address in another country than the tenant's ({@link isTenantCountry}):
   * those fields then accept anything, so customers from abroad can enter their real address
   * whatever its local shape. This mirrors the legacy checkout, which drops the required, format
   * and Packstation checks for a foreign country alike and leaves the address to the backend
   * (fragment-order-checkout/src/components/RegisterForm/RegistrationFormContext.tsx).
   *
   * Everything that is not part of the address – gender, names, phone number – stays validated.
   */
  validateAddressFields?: boolean;
};

export const addressValidationSchema = (
  markFieldsMandatory: boolean,
  { validateAddressFields = true }: AddressValidationSchemaOptions = {},
) =>
  z.object({
    gender: markFieldsMandatory
      ? z
          .string({
            error: (issue) => (issue.input === undefined ? FieldErrorReason.required : undefined),
          })
          .min(1, { error: FieldErrorReason.required })
      : z.string().optional(),
    firstName: markFieldsMandatory
      ? z
          .string()
          .min(1, { error: FieldErrorReason.required })
          .min(NAME_MIN_LENGTH, { error: FieldErrorReason.length })
          .max(NAME_MAX_LENGTH, { error: FieldErrorReason.length })
          .regex(nameRegex, { error: FieldErrorReason.invalid })
      : z.string().optional(),
    lastName: markFieldsMandatory
      ? z
          .string()
          .min(1, { error: FieldErrorReason.required })
          .min(NAME_MIN_LENGTH, { error: FieldErrorReason.length })
          .max(NAME_MAX_LENGTH, { error: FieldErrorReason.length })
          .regex(nameRegex, { error: FieldErrorReason.invalid })
      : z.string().optional(),
    street: markFieldsMandatory
      ? addressField(
          validateAddressFields,
          [
            [addressTextRegex, FieldErrorReason.invalid],
            [parcelShopRegex, FieldErrorReason.streetPP],
          ],
          ADDRESS_TEXT_MAX_LENGTH,
        )
      : z.string().optional(),
    houseNumber: markFieldsMandatory
      ? houseNumberField(validateAddressFields)
      : z.string().optional(),
    additional: z.string().optional(),
    zipCode: markFieldsMandatory
      ? addressField(validateAddressFields, [[zipCodeRegex, FieldErrorReason.invalid]])
      : z.string().optional(),
    city: markFieldsMandatory
      ? addressField(
          validateAddressFields,
          [[addressTextRegex, FieldErrorReason.invalid]],
          ADDRESS_TEXT_MAX_LENGTH,
        )
      : z.string().optional(),
    primaryPhoneNumber: z
      .string()
      .regex(phoneNumberCharacterPattern, { error: FieldErrorReason.invalid })
      .optional()
      .or(z.literal('')),
  });
